/**
 * RequireRole — reusable route guard for the DIoT Self-Service Portal.
 *
 * Restricts access to routes/children to a set of allowed central roles.
 * The guard is expressed in NUMERIC role-id terms (per the DuoLift
 * `DUO_USER_ROLE` table) so callers use the same numeric set the backend
 * `authorize()` enforces: `{1, 3, 5}` = RpcAdmin / CustomerAdmin / LocationAdmin.
 *
 * Role resolution is delegated entirely to the `useUserRole` resolver (the
 * single source of truth). This component does NOT read redux or reconcile
 * role signals itself — it only decides allow / block / wait based on the
 * normalized numeric role id the resolver returns. That keeps role-source
 * knowledge in one place instead of scattering inline
 * `Constants.userRole === ...` checks across guards and pages.
 *
 * Loading behavior mirrors ValueDashboard: while the role has not loaded
 * (`resolver.loaded === false`) we render nothing rather than flash-redirecting
 * an authorized user to home.
 *
 * @module RequireRole
 */

import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import useUserRole, { ROLE_ID_TO_NAME } from "../hooks/useUserRole";

// Re-export the mapping so existing importers of RequireRole keep working.
export { ROLE_ID_TO_NAME };

export default function RequireRole({ roles = [], children }) {
  const { roleId, loaded } = useUserRole();

  // Don't flash-redirect while the role is still loading.
  if (!loaded) {
    return null;
  }

  const isAllowed = roleId !== null && roles.includes(roleId);

  if (!isAllowed) {
    // Unauthorized -> send home, matching how existing pages fall back to "/".
    return <Navigate to="/" replace />;
  }

  // Support both nested-route (layout) usage via <Outlet /> and direct children.
  return children ? children : <Outlet />;
}
